<template>
  <a
    class="float-home"
    :class="{ 'float-home--dragging': dragging, 'float-home--moved': !!position }"
    href="/"
    :style="dragStyle"
    :aria-label="ariaLabel"
    :title="ariaLabel"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @click="onClick"
  >
    <span class="float-home__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M3.2 10.8 12 3.6l8.8 7.2" />
        <path d="M5.9 9.6V20.4h12.2V9.6" />
        <path d="M9.9 20.4v-5.7h4.2v5.7" />
      </svg>
    </span>
    <span class="float-home__text">{{ $store.getters.t('home') }}</span>
  </a>
</template>

<script>
// Touch/pen dragging is enabled on small screens only; a plain tap still navigates.
const MOBILE_QUERY = '(max-width: 768px)'
const DRAG_THRESHOLD = 5

export default {
  name: 'FloatingHomeButton',
  data () {
    return {
      dragging: false,
      moved: false,
      position: null,
      pointerId: null,
      start: { x: 0, y: 0 },
      origin: { left: 0, top: 0 },
      size: { width: 0, height: 0 }
    }
  },
  computed: {
    ariaLabel () { return this.$store.getters.t('backHome') },
    dragStyle () {
      if (!this.position) return null
      return {
        left: `${this.position.left}px`,
        top: `${this.position.top}px`,
        right: 'auto',
        bottom: 'auto',
        animation: 'none'
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    window.addEventListener('orientationchange', this.onResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('orientationchange', this.onResize)
  },
  methods: {
    isMobile () {
      return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
    },
    onPointerDown (event) {
      if (!this.isMobile()) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      const rect = this.$el.getBoundingClientRect()
      this.start = { x: event.clientX, y: event.clientY }
      this.origin = { left: rect.left, top: rect.top }
      this.size = { width: rect.width, height: rect.height }
      this.dragging = true
      this.moved = false
      this.pointerId = event.pointerId === undefined ? null : event.pointerId
      if (!this.position) this.position = { left: rect.left, top: rect.top }
      if (this.pointerId !== null && this.$el.setPointerCapture) {
        try { this.$el.setPointerCapture(this.pointerId) } catch (error) { /* capture is optional */ }
      }
    },
    onPointerMove (event) {
      if (!this.dragging) return
      const dx = event.clientX - this.start.x
      const dy = event.clientY - this.start.y
      if (!this.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return
      this.moved = true
      const maxLeft = Math.max(0, window.innerWidth - this.size.width)
      const maxTop = Math.max(0, window.innerHeight - this.size.height)
      this.position = {
        left: Math.min(Math.max(0, this.origin.left + dx), maxLeft),
        top: Math.min(Math.max(0, this.origin.top + dy), maxTop)
      }
    },
    onPointerUp () {
      if (!this.dragging) return
      this.dragging = false
      if (this.pointerId !== null && this.$el.releasePointerCapture) {
        try { this.$el.releasePointerCapture(this.pointerId) } catch (error) { /* already released */ }
      }
      this.pointerId = null
    },
    onClick (event) {
      // A drag must not be treated as a navigation. Owning the click here (rather
      // than using router-link) keeps the guard ahead of any router handling.
      if (this.moved) {
        this.moved = false
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
      event.preventDefault()
      if (this.$route.path !== '/') this.$router.push('/')
    },
    onResize () {
      if (!this.position) return
      if (!this.isMobile()) {
        this.position = null
        return
      }
      const maxLeft = Math.max(0, window.innerWidth - this.size.width)
      const maxTop = Math.max(0, window.innerHeight - this.size.height)
      this.position = {
        left: Math.min(Math.max(0, this.position.left), maxLeft),
        top: Math.min(Math.max(0, this.position.top), maxTop)
      }
    }
  }
}
</script>
